import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import AdminSidebar from '../components/AdminSidebar';
import {
  Users,
  Upload,
  HardDrive,
  Activity,
  Clock,
  FileVideo,
  File,
  TrendingUp,
  RefreshCw,
  Ban,
  ChevronDown,
  Download
} from 'lucide-react';
import { downloadUpload } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rejectMenuOpen, setRejectMenuOpen] = useState(null);

  const REJECTION_REASONS = [
    "Show Not Titled Correctly. Please use this format: Sunday March 11th at 3:30 PM - Pastor John Doe",
    "Show Is not the correct length. Show must be either 28:30 or 58:30 for hour long broadcasts"
  ];

  const handleReject = async (uploadId, reason) => {
    try {
      await axios.post(`${API}/admin/uploads/${uploadId}/reject`, 
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Upload rejected');
      setRejectMenuOpen(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to reject upload');
    }
  };

  const handleDownload = (uploadId, filename) => downloadUpload(uploadId, filename, token);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, uploadsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/uploads?limit=5`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/logs?limit=10`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setRecentUploads(uploadsRes.data);
      setRecentLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'mxf', 'mts'];
    if (videoExts.includes(ext)) {
      return <FileVideo className="w-4 h-4 text-red-500" />;
    }
    return <File className="w-4 h-4 text-zinc-500" />;
  };

  const getActionColor = (action) => {
    if (action.includes('login')) return 'text-blue-400';
    if (action.includes('upload')) return 'text-green-400';
    if (action.includes('delete')) return 'text-red-400';
    if (action.includes('create')) return 'text-yellow-400';
    if (action.includes('update')) return 'text-purple-400';
    return 'text-zinc-400';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-dashboard">
      <AdminSidebar />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto pt-16 lg:pt-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-display text-2xl sm:text-4xl text-white tracking-tight">DASHBOARD</h1>
              <p className="text-zinc-500 mt-1 text-sm sm:text-base">Overview of your upload portal</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 w-full sm:w-auto"
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl" data-testid="stat-total-users">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider">Total Users</p>
                    <p className="text-2xl sm:text-3xl font-display text-white mt-1 sm:mt-2">{stats?.total_users || 0}</p>
                    <p className="text-zinc-500 text-xs mt-1">{stats?.active_users || 0} active</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/10 flex items-center justify-center rounded">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl" data-testid="stat-total-uploads">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider">Total Uploads</p>
                    <p className="text-2xl sm:text-3xl font-display text-white mt-1 sm:mt-2">{stats?.total_uploads || 0}</p>
                    <p className="text-zinc-500 text-xs mt-1">{stats?.recent_uploads || 0} this week</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/10 flex items-center justify-center rounded">
                    <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl" data-testid="stat-storage-used">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider">Storage Used</p>
                    <p className="text-xl sm:text-3xl font-display text-white mt-1 sm:mt-2">
                      {formatBytes(stats?.total_size_bytes || 0)}
                    </p>
                    <p className="text-zinc-500 text-xs mt-1">Across all users</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/10 flex items-center justify-center rounded">
                    <HardDrive className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl" data-testid="stat-weekly-activity">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider">Weekly Activity</p>
                    <p className="text-2xl sm:text-3xl font-display text-white mt-1 sm:mt-2">{stats?.recent_uploads || 0}</p>
                    <p className="text-zinc-500 text-xs mt-1">Uploads this week</p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500/10 flex items-center justify-center rounded">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Recent Uploads */}
            <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl">
              <CardHeader className="border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-lg text-white tracking-tight">
                    RECENT UPLOADS
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin/logs')}
                    className="text-zinc-400 hover:text-white text-xs"
                  >
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[300px]">
                  {recentUploads.length === 0 ? (
                    <div className="p-6 text-center text-zinc-500">
                      <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No uploads yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-800">
                      {recentUploads.map(upload => (
                        <div key={upload.id} className="p-4 hover:bg-zinc-800/50 transition-colors">
                          <div className="flex items-start gap-3">
                            {getFileIcon(upload.filename)}
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-sm font-mono truncate">{upload.filename}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                                <span>{upload.username}</span>
                                <span>•</span>
                                <span>{formatBytes(upload.size_bytes)}</span>
                                <span>•</span>
                                <span>{formatDate(upload.completed_at || upload.created_at)}</span>
                              </div>
                              {upload.processing_status === 'rejected' && upload.rejection_reason && (
                                <p className="mt-1 text-xs text-red-400 italic">{upload.rejection_reason}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {upload.file_exists && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownload(upload.id, upload.filename)}
                                  className="h-7 px-2 text-zinc-500 hover:text-green-400 hover:bg-green-500/10"
                                  data-testid={`download-btn-${upload.id}`}
                                >
                                  <Download className="w-3.5 h-3.5 mr-1" />
                                  <span className="text-xs">Download</span>
                                </Button>
                              )}
                              {upload.processing_status === 'rejected' ? (
                                <span className="px-2 py-0.5 text-xs uppercase font-medium bg-red-500/20 text-red-400 rounded">
                                  Rejected
                                </span>
                              ) : (
                                <>
                                  <span className={`px-2 py-0.5 text-xs uppercase ${
                                    upload.processing_status === 'failed' || upload.status === 'failed' ? 'status-failed' : 'status-completed'
                                  }`}>
                                    {upload.processing_status === 'complete' ? 'Ready' : upload.processing_status || upload.status}
                                  </span>
                                  <div className="relative">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setRejectMenuOpen(rejectMenuOpen === upload.id ? null : upload.id)}
                                      className="h-7 px-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                                      data-testid={`reject-btn-${upload.id}`}
                                    >
                                      <Ban className="w-3.5 h-3.5 mr-1" />
                                      <span className="text-xs">Reject</span>
                                      <ChevronDown className="w-3 h-3 ml-1" />
                                    </Button>
                                    {rejectMenuOpen === upload.id && (
                                      <div className="absolute right-0 top-full mt-1 w-80 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                                        <p className="px-3 py-2 text-xs text-zinc-400 uppercase tracking-wider border-b border-zinc-800">Select Rejection Reason</p>
                                        {REJECTION_REASONS.map((reason, idx) => (
                                          <button
                                            key={idx}
                                            onClick={() => handleReject(upload.id, reason)}
                                            className="w-full text-left px-3 py-3 text-sm text-zinc-300 hover:bg-red-500/10 hover:text-red-400 transition-colors border-b border-zinc-800 last:border-0"
                                            data-testid={`reject-reason-${idx}`}
                                          >
                                            {reason}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Recent Activity Logs */}
            <Card className="bg-zinc-900/50 border-zinc-800 rounded-xl">
              <CardHeader className="border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-lg text-white tracking-tight">
                    ACTIVITY LOG
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin/logs')}
                    className="text-zinc-400 hover:text-white text-xs"
                  >
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[300px]">
                  {recentLogs.length === 0 ? (
                    <div className="p-6 text-center text-zinc-500">
                      <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No activity yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-zinc-800">
                      {recentLogs.map(log => (
                        <div key={log.id} className="p-4 hover:bg-zinc-800/50 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className={`w-2 h-2 mt-1.5 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                                  {log.action}
                                </span>
                                {log.username && (
                                  <span className="text-zinc-500 text-xs">by {log.username}</span>
                                )}
                              </div>
                              <p className="text-zinc-400 text-xs mt-1 truncate">{log.details}</p>
                              <div className="flex items-center gap-1 mt-1 text-xs text-zinc-600">
                                <Clock className="w-3 h-3" />
                                {formatDate(log.timestamp)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              onClick={() => navigate('/admin/users')}
              className="bg-zinc-800 hover:bg-zinc-700 text-white w-full sm:w-auto"
              data-testid="manage-users-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Manage Users
            </Button>
            <Button
              onClick={() => navigate('/admin/settings')}
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 w-full sm:w-auto"
              data-testid="branding-settings-btn"
            >
              Branding Settings
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
